import supabase from '@/lib/supabase';
import { PetAssets } from '@/constant/schema';

export interface OutfitChangeItem {
  slot: string;
  assetId: number;
  name: string;
}

export interface OutfitChangeReplacement extends OutfitChangeItem {
  oldAssetId: number;
  oldName: string;
}

export interface OutfitChange {
  // Put on: new items in slots that were previously empty
  putOn: OutfitChangeItem[];
  // Changed to: replaced old item with new item in the same slot
  replacements: OutfitChangeReplacement[];
  // Took off: removed items from slots
  removed: OutfitChangeItem[];
  timestamp: string;
}

export interface PurchaseItem {
  assetId: number;
  name: string;
  part: string;
  price: number;
}

export interface PurchaseEvent {
  items: PurchaseItem[];
  totalPrice: number;
  timestamp: string;
}

/**
 * Calculate the diff between old and new outfit
 * Distinguishes between:
 * - putOn: Items added to previously empty slots
 * - replacements: Items that replaced other items in the same slot
 * - removed: Items that were removed from slots
 */
export function calculateOutfitDiff(
  oldOutfit: Record<string, number>,
  newOutfit: Record<string, number>,
  assetDetails: Record<number, { name: string; description: string }>
): OutfitChange {
  const putOn: OutfitChangeItem[] = [];
  const replacements: OutfitChangeReplacement[] = [];
  const removed: OutfitChangeItem[] = [];

  // Check all slots for changes
  const allSlots = new Set([...Object.keys(oldOutfit), ...Object.keys(newOutfit)]);

  for (const slot of allSlots) {
    const oldId = oldOutfit[slot];
    const newId = newOutfit[slot];

    if (oldId && newId && oldId !== newId) {
      // Slot had an item, and it was replaced with a different item
      replacements.push({
        slot,
        assetId: newId,
        name: assetDetails[newId]?.name || `Item ${newId}`,
        oldAssetId: oldId,
        oldName: assetDetails[oldId]?.name || `Item ${oldId}`,
      });
    } else if (!oldId && newId) {
      // Slot was empty, now has an item (put on)
      putOn.push({
        slot,
        assetId: newId,
        name: assetDetails[newId]?.name || `Item ${newId}`,
      });
    } else if (oldId && !newId) {
      // Slot had an item, now empty (removed)
      removed.push({
        slot,
        assetId: oldId,
        name: assetDetails[oldId]?.name || `Item ${oldId}`,
      });
    }
  }

  return {
    putOn,
    replacements,
    removed,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Generate outfit change message content with detailed context
 * Example: "User changed outfit: Put on Santa Hat (head), Changed to Red Scarf (top) from Blue Scarf, Took off Baseball Cap (old head)"
 */
function generateOutfitChangeMessage(changes: OutfitChange): string {
  const parts: string[] = [];

  // Put on: new items in previously empty slots
  if (changes.putOn.length > 0) {
    const items = changes.putOn
      .map((c) => `Put on ${c.name} (${c.slot})`)
      .join(', ');
    parts.push(items);
  }

  // Changed to: replacements with old item reference
  if (changes.replacements.length > 0) {
    const items = changes.replacements
      .map((c) => `Changed to ${c.name} (${c.slot}) from ${c.oldName}`)
      .join(', ');
    parts.push(items);
  }

  // Took off: removed items
  if (changes.removed.length > 0) {
    const items = changes.removed
      .map((c) => `Took off ${c.name} (${c.slot})`)
      .join(', ');
    parts.push(items);
  }

  if (parts.length === 0) {
    return 'No outfit changes recorded';
  }

  return `User changed outfit: ${parts.join(', ')}`;
}

/**
 * Generate purchase message content with detailed context
 * Example: "User purchased 3 items for 150 gems: Astronaut Helmet (head, 50 gems), Space Suit (top, 70 gems), Jetpack (back, 30 gems)"
 */
function generatePurchaseMessage(event: PurchaseEvent): string {
  const itemDetails = event.items
    .map((item) => `${item.name} (${item.part}, ${item.price} gems)`)
    .join(', ');

  return `User purchased ${event.items.length} item(s) for ${event.totalPrice} gems: ${itemDetails}`;
}

/**
 * Insert outfit change message to pet_chat_messages
 */
export async function insertOutfitChangeMessage(
  userId: string,
  changes: OutfitChange
): Promise<void> {
  try {
    const content = generateOutfitChangeMessage(changes);

    const { error } = await supabase.from('pet_chat_messages').insert({
      id: crypto.randomUUID(),
      user_id: userId,
      thread_id: userId,
      message_type: 'user',
      content: content,
      metadata: {
        type: 'outfit_change',
        changes: changes,
      },
    });

    if (error) {
      console.error('Error inserting outfit change message:', error);
    }
  } catch (err) {
    console.error('Error in insertOutfitChangeMessage:', err);
  }
}

/**
 * Insert purchase message to pet_chat_messages
 */
export async function insertPurchaseMessage(
  userId: string,
  items: PurchaseItem[],
  totalPrice: number
): Promise<void> {
  try {
    const event: PurchaseEvent = {
      items,
      totalPrice,
      timestamp: new Date().toISOString(),
    };

    const content = generatePurchaseMessage(event);

    const { error } = await supabase.from('pet_chat_messages').insert({
      id: crypto.randomUUID(),
      user_id: userId,
      thread_id: userId,
      message_type: 'user',
      content: content,
      metadata: {
        type: 'purchase',
        event: event,
      },
    });

    if (error) {
      console.error('Error inserting purchase message:', error);
    }
  } catch (err) {
    console.error('Error in insertPurchaseMessage:', err);
  }
}

/**
 * Fetch asset details for given asset IDs
 */
export async function fetchAssetDetails(
  assetIds: number[]
): Promise<Record<number, { name: string; description: string }>> {
  try {
    const { data, error } = await supabase
      .from('pet_assets')
      .select('id, name, description')
      .in('id', assetIds);

    if (error) {
      console.error('Error fetching asset details:', error);
      return {};
    }

    return Object.fromEntries(
      data?.map((a) => [a.id, { name: a.name || 'Unknown', description: a.description || '' }]) || []
    );
  } catch (err) {
    console.error('Error in fetchAssetDetails:', err);
    return {};
  }
}
